import './jjb-3d-file-gallery-activity.js';

const { html, render, useCallback, useEffect, useMemo, useRef, useState } =
  window.htmPreact || {};

if (!window.htmPreact) {
  throw new Error(
    '[jjb-3d-file-gallery] Missing global htmPreact. Load preact-standalone.umd.js before this script.',
  );
}

function isTest() {
  return (
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    window.location.hostname.includes('shopifypreview.com') ||
    localStorage.getItem('_shop_mode') === 'test'
  );
}

const IS_TEST = isTest();
const API_BASE = IS_TEST ? '/apps/ai-test/api' : '/apps/ai';
const PLACEHOLDER_IMG =
  'https://cdn.shopify.com/s/files/1/0568/6227/7806/files/placeholder-image.png?v=1652339843';
const RECORD_STATUS = {
  IMAGE_GENERATING: 1,
  IMAGE_COMPLETED: 2,
  MODEL_GENERATING: 3,
  MODEL_COMPLETED: 4,
  FAILED: 5,
};
const RECORDS_PAGE_SIZE = 50;
const LOAD_MORE_THRESHOLD_PX = 96;

const {
  buildActivityAssetPath,
  buildActivityTaskListPath,
  getMergeIdentity,
  mapActivityTasksToRecords,
} = globalThis.jjbActivityGalleryApi;

const fallbackIdentity = {
  customerId: window.theme?.customerId
    ? String(window.theme.customerId)
    : undefined,
  anonymousId: `${Math.random().toString(36).slice(2)}-${Date.now()}-back`,
};
const getIdentity = () => window.jjbUtils?.getIdentity?.() || fallbackIdentity;

function withTestBypass(path) {
  if (!IS_TEST) return path;
  const [pathname, hash = ''] = path.split('#');
  const [basePath, queryString = ''] = pathname.split('?');
  const params = new URLSearchParams(queryString);
  const query = params.toString();
  const nextPath = query ? `${basePath}?${query}` : basePath;
  return hash ? `${nextPath}#${hash}` : nextPath;
}

async function request(path, options = {}) {
  const { method = 'GET', body, ...rest } = options;
  return fetch(`${API_BASE}${withTestBypass(path)}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });
}

async function unwrapResponse(response) {
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const result = await response.json();
  if (result?.success === false) {
    throw new Error(result.errorMessage || result.error || 'Request failed');
  }
  return result?.data;
}

async function mergeTasks(identity) {
  const mergeIdentity = getMergeIdentity(identity);
  if (!mergeIdentity) return;
  const response = await request('/activity/tasks/merge', {
    method: 'POST',
    body: mergeIdentity,
  });
  await unwrapResponse(response);
}

async function getRecords({
  identity = getIdentity(),
  pageSize = RECORDS_PAGE_SIZE,
  cursor,
} = {}) {
  const safePageSize = Math.min(
    Math.max(Number(pageSize) || RECORDS_PAGE_SIZE, 1),
    RECORDS_PAGE_SIZE,
  );
  const path = new URL(
    buildActivityTaskListPath(identity, safePageSize),
    window.location.origin,
  );
  if (cursor) path.searchParams.set('cursor', cursor);
  const response = await request(`${path.pathname}${path.search}`);
  const data = await unwrapResponse(response);
  const list = mapActivityTasksToRecords(data?.tasks);
  list.forEach((record) => {
    if (record?.modelUrl) cache3dModel.fetchAndCache(record.modelUrl);
  });
  return {
    list,
    nextCursor: data?.nextCursor || null,
    hasMore: Boolean(data?.hasMore && data?.nextCursor),
    total: data?.totalTasksNum ?? list.length,
  };
}

async function deleteRecord({ assetId, identity = getIdentity() }) {
  const response = await request(buildActivityAssetPath(assetId), {
    method: 'DELETE',
    body: identity,
  });
  await unwrapResponse(response);
  return { success: true };
}

class Cache3dModel {
  constructor() {
    this.models = new Map();
  }

  async fetchAndCache(modelUrl) {
    if (!modelUrl || this.models.has(modelUrl)) return true;
    this.models.set(modelUrl, true);
    fetch(modelUrl).catch(() => {});
    return true;
  }
}

const cache3dModel = new Cache3dModel();

function getCurrentVariantId(hostVariantId) {
  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant) return Number(urlVariant);

  const formVariant = document.querySelector(
    'form[action*="/cart/add"] [name="id"]',
  )?.value;
  if (formVariant) return Number(formVariant);

  return hostVariantId ? Number(hostVariantId) : 0;
}

function ga4(eventName, params = {}, options = {}) {
  try {
    const payload = {
      ...params,
      diy_module: '3d_file_gallery',
      page: location.pathname,
    };
    if (eventName && eventName.indexOf('POD_') === 0 && window.GOOGLE_ADS_ID) {
      payload.google_ads_id = window.GOOGLE_ADS_ID;
    }
    window?.zlog?.track?.(eventName, payload, {
      includeAds: Boolean(options.includeAds),
      statsig: false,
    });
  } catch (_) {}
}

function completedRecords(records) {
  return records.filter(
    (record) =>
      record?.imageUrl &&
      (record.status === RECORD_STATUS.IMAGE_COMPLETED ||
        record.status === RECORD_STATUS.MODEL_COMPLETED ||
        record.modelUrl),
  );
}

function App({ host }) {
  const hostVariantId = host.dataset.variantId || '';
  const hostVariantAvailable = host.dataset.variantAvailable === 'true';
  const templateId = host.dataset.templateId || 'stl';
  const productTitle = host.dataset.productTitle || document.title;
  const initialVariantId = getCurrentVariantId(hostVariantId);
  const canPurchaseVariant = Boolean(initialVariantId && hostVariantAvailable);
  const [records, setRecords] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [activeView, setActiveView] = useState('3d');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [message, setMessage] = useState('');
  const [adding, setAdding] = useState(false);
  const [modelLoadStatusById, setModelLoadStatusById] = useState({});
  const loadingMoreRef = useRef(false);

  const galleryRecords = useMemo(() => completedRecords(records), [records]);
  const currentRecord = useMemo(
    () => galleryRecords.find((record) => record.id === currentId) || null,
    [galleryRecords, currentId],
  );

  useEffect(() => {
    let active = true;
    const identity = getIdentity();
    mergeTasks(identity)
      .catch((error) => {
        console.warn('[jjb-3d-file-gallery] identity merge failed', error);
      })
      .then(() => getRecords({ identity, pageSize: RECORDS_PAGE_SIZE }))
      .then((result) => {
        if (!active) return;
        const nextRecords = completedRecords(result.list);
        setRecords(nextRecords);
        setNextCursor(result.nextCursor);
        setHasMore(result.hasMore);
        const first3d =
          nextRecords.find((record) => record.modelUrl) ||
          nextRecords[0] ||
          null;
        if (first3d) {
          setCurrentId(first3d.id);
          setActiveView(first3d.modelUrl ? '3d' : '2d');
        }
      })
      .catch((error) => {
        console.warn('[jjb-3d-file-gallery] failed to load records', error);
        setMessage(
          'Unable to load your gallery. Please refresh and try again.',
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadNextPage = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || !nextCursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await getRecords({
        cursor: nextCursor,
        pageSize: RECORDS_PAGE_SIZE,
      });
      const nextRecords = completedRecords(result.list);
      setRecords((current) => {
        const currentIds = new Set(current.map((record) => record.id));
        return [
          ...current,
          ...nextRecords.filter((record) => !currentIds.has(record.id)),
        ];
      });
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (error) {
      console.warn(
        '[jjb-3d-file-gallery] failed to load more records',
        error,
      );
      setMessage('Unable to load more history items. Please try again.');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, nextCursor]);

  useEffect(() => {
    if (!loading && !galleryRecords.length && hasMore && nextCursor) {
      loadNextPage();
    }
  }, [
    galleryRecords.length,
    hasMore,
    loadNextPage,
    loading,
    nextCursor,
  ]);

  const handleHistoryScroll = useCallback(
    (event) => {
      const list = event.currentTarget;
      const remainingScroll =
        list.scrollWidth - list.clientWidth - list.scrollLeft;
      if (remainingScroll <= LOAD_MORE_THRESHOLD_PX) loadNextPage();
    },
    [loadNextPage],
  );

  useEffect(() => {
    ga4('POD_diy_view', { template_id: templateId });
  }, [templateId]);

  useEffect(() => {
    if (!currentRecord) return;
    if (activeView === '2d' && currentRecord.imageUrl) {
      ga4('diy_2D_impression', { template_id: templateId });
    }
    if (activeView === '3d' && currentRecord.modelUrl) {
      ga4('diy_3D_impression', { template_id: templateId });
    }
  }, [
    activeView,
    currentRecord?.id,
    currentRecord?.imageUrl,
    currentRecord?.modelUrl,
    templateId,
  ]);

  const selectRecord = (record) => {
    setCurrentId(record.id);
    setActiveView(record.modelUrl ? '3d' : '2d');
    setMessage('');
    ga4('diy_history_click', { template_id: templateId });
  };

  const setModelLoadStatus = (id, status) => {
    if (!id) return;
    setModelLoadStatusById((current) => ({ ...current, [id]: status }));
  };

  const removeRecord = async (recordId) => {
    if (!recordId) return;
    if (!window.confirm('Delete this history record?')) return;
    try {
      if (!String(recordId).startsWith('pending_')) {
        const result = await deleteRecord({ assetId: recordId });
        if (!result?.success) throw new Error(result?.error || 'Delete failed');
      }
      setRecords((current) => {
        const nextRecords = current.filter((record) => record.id !== recordId);
        if (recordId === currentId) {
          const nextCurrent =
            completedRecords(nextRecords).find((record) => record.modelUrl) ||
            completedRecords(nextRecords)[0] ||
            null;
          setCurrentId(nextCurrent?.id || null);
          setActiveView(nextCurrent?.modelUrl ? '3d' : '2d');
        }
        return nextRecords;
      });
    } catch (error) {
      console.warn('[jjb-3d-file-gallery] delete failed', error);
      setMessage('Failed to delete record. Please try again.');
    }
  };

  const addToCart = async (checkout = false) => {
    if (!currentRecord?.modelUrl) {
      setMessage('Please choose a gallery item with a ready 3D model.');
      return;
    }

    const variantId = getCurrentVariantId(hostVariantId);
    if (!variantId) {
      setMessage(
        'Unable to find the product variant. Please refresh and try again.',
      );
      return;
    }

    setAdding(true);
    setMessage('');
    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/add.js`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  'Generated Image': currentRecord.imageUrl,
                  _3d_model_url: currentRecord.modelUrl,
                  '_Record ID': String(currentRecord.id),
                  '_Created From': 'jjb_3d_file_gallery',
                },
              },
            ],
          }),
        },
      );
      if (!response.ok) throw new Error('Failed to add to cart');

      ga4(checkout ? 'POD_Checkout' : 'POD_AddToCart', {
        template_id: templateId,
        record_id: currentRecord.id,
        model_url: currentRecord.modelUrl,
      });
      ga4(
        checkout ? 'begin_checkout' : 'add_to_cart',
        {
          currency: window.Shopify?.currency?.active || 'USD',
          items: [
            {
              item_id: String(variantId),
              item_name: productTitle,
              quantity: 1,
            },
          ],
          template_id: templateId,
        },
        { includeAds: true },
      );

      if (checkout) {
        window.location.href = '/checkout';
      } else {
        await window.jjbUtils?.openCartDrawer();
        setMessage('Added to cart.');
      }
    } catch (error) {
      console.error('[jjb-3d-file-gallery] cart error', error);
      setMessage('Unable to add this 3D file to cart. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const modelLoadStatus = currentRecord?.id
    ? modelLoadStatusById[currentRecord.id]
    : null;

  if (loading) {
    return html`<div class="jjb-3d-file-gallery__loading">
      Loading 3D gallery...
    </div>`;
  }

  if (!galleryRecords.length) {
    if (hasMore || loadingMore) {
      return html`<div class="jjb-3d-file-gallery__loading">
        Loading 3D gallery...
      </div>`;
    }
    return html`
      <div class="jjb-3d-file-gallery__empty">
        No 3D gallery items are ready yet.
      </div>
    `;
  }

  return html`
    <div class="jjb-3d-file-gallery__shell">
      <div class="jjb-3d-file-gallery__viewer">
        <div class="jjb-3d-file-gallery__view-card">
          <div class="jjb-3d-file-gallery__tabs">
            <button
              type="button"
              class="jjb-3d-file-gallery__tab ${activeView === '2d'
                ? 'is-active'
                : ''}"
              onClick=${() => setActiveView('2d')}
            >
              2D Render
            </button>
            <button
              type="button"
              class="jjb-3d-file-gallery__tab ${activeView === '3d'
                ? 'is-active'
                : ''}"
              disabled=${!currentRecord?.modelUrl}
              onClick=${() => currentRecord?.modelUrl && setActiveView('3d')}
            >
              3D Model
            </button>
          </div>

          <div class="jjb-3d-file-gallery__view" hidden=${activeView !== '2d'}>
            <img
              class="jjb-3d-file-gallery__image"
              src=${currentRecord?.imageUrl || PLACEHOLDER_IMG}
              alt="2D preview"
              width="600"
              height="600"
              loading="lazy"
            />
          </div>

          <div class="jjb-3d-file-gallery__view" hidden=${activeView !== '3d'}>
            ${currentRecord?.modelUrl
              ? html`
                  ${modelLoadStatus !== 'success'
                    ? html`<div class="jjb-3d-file-gallery__model-loading">
                        ${modelLoadStatus === 'error'
                          ? html`<div class="jjb-3d-file-gallery__model-error">
                              Taking longer than expected. Please try again.
                            </div>`
                          : html`<div
                                class="jjb-3d-file-gallery__spinner"
                              ></div>
                              <div class="jjb-3d-file-gallery__loading-text">
                                Loading 3D Model...
                              </div>`}
                      </div>`
                    : null}
                  <model-viewer
                    src=${currentRecord.modelUrl}
                    ar
                    ar-modes="webxr scene-viewer quick-look"
                    camera-controls
                    orientation="0 0 -90deg"
                    auto-rotate
                    tone-mapping="neutral"
                    shadow-intensity="1"
                    onLoad=${() =>
                      setModelLoadStatus(currentRecord.id, 'success')}
                    onError=${() =>
                      setModelLoadStatus(currentRecord.id, 'error')}
                  ></model-viewer>
                `
              : html`<div class="jjb-3d-file-gallery__notice">
                  This gallery item does not have a ready 3D model.
                </div>`}
          </div>
        </div>
      </div>

      <div class="jjb-3d-file-gallery__history">
        <div class="jjb-3d-file-gallery__history-title">
          History
          <span class="jjb-3d-file-gallery__history-count">
            (${galleryRecords.length})
          </span>
        </div>
        <div
          class="jjb-3d-file-gallery__history-list"
          onScroll=${handleHistoryScroll}
        >
          ${galleryRecords.map(
            (record) => html`
              <div
                class="jjb-3d-file-gallery__history-item ${record.id ===
                currentId
                  ? 'is-active'
                  : ''}"
                role="button"
                tabindex="0"
                onClick=${() => selectRecord(record)}
                onKeyDown=${(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  selectRecord(record);
                }}
                aria-label="Select gallery history item"
              >
                <img
                  src=${record.imageUrl || PLACEHOLDER_IMG}
                  alt=""
                  width="64"
                  height="64"
                  loading="lazy"
                />
                <button
                  type="button"
                  class="jjb-3d-file-gallery__history-delete"
                  onClick=${(event) => {
                    event.stopPropagation();
                    removeRecord(record.id);
                  }}
                  aria-label="Delete history item"
                >
                  ×
                </button>
              </div>
            `,
          )}
          ${hasMore
            ? html`<div
                class="jjb-3d-file-gallery__history-load-more ${loadingMore
                  ? 'is-loading'
                  : ''}"
                role="status"
                aria-label=${loadingMore
                  ? 'Loading more history items'
                  : undefined}
              >
                ${loadingMore
                  ? html`<span
                      class="jjb-3d-file-gallery__spinner"
                      aria-hidden="true"
                    ></span>`
                  : null}
              </div>`
            : null}
        </div>
      </div>

      <div
        class="jjb-3d-file-gallery__actions payment-buttons ${!canPurchaseVariant
          ? 'jjb-3d-file-gallery__actions--sold-out'
          : ''}"
      >
        ${!canPurchaseVariant
          ? html`<button
              type="button"
              class="btn btn--full add-to-cart add-to-cart--secondary"
              disabled
            >
              Sold Out
            </button>`
          : html`<button
                type="button"
                class="btn btn--full add-to-cart add-to-cart--secondary"
                disabled=${adding || !currentRecord?.modelUrl}
                onClick=${() => addToCart(false)}
              >
                ${adding ? 'Adding...' : 'Add to Cart'}
              </button>
              <button
                type="button"
                class="btn btn--full shopify-payment-button__button shopify-payment-button__button--unbranded"
                disabled=${adding || !currentRecord?.modelUrl}
                onClick=${() => addToCart(true)}
              >
                Buy Now
              </button>`}
        <div class="jjb-3d-file-gallery__message" role="status">${message}</div>
      </div>
    </div>
  `;
}

class JJB3DFileGallery extends HTMLElement {
  connectedCallback() {
    render(html`<${App} host=${this} />`, this);
  }

  disconnectedCallback() {
    render(null, this);
  }
}

if (!customElements.get('jjb-3d-file-gallery')) {
  customElements.define('jjb-3d-file-gallery', JJB3DFileGallery);
}
